import type {
  VerificationCheckErrorCategory,
  VerificationRunCheck,
} from 'api/bitedb/entities/businessVerification.service';

import type {
  VerificationReportCheck,
  VerificationReportCheckError,
} from './entityVerificationReportModel';

/**
 * What a reviewer is told about a Check Error, wherever they are told it: the
 * verification page, the record band, the after-run snackbars and the PDF.
 * The provider and the exact cause are never named; those go to the service
 * logs.
 */

const PROVIDER_DID_NOT_RESPOND: VerificationCheckErrorCategory[] = [
  'provider_unavailable',
  'rate_limited',
];

/** Whether trying again later may help, as opposed to a fault on our side. */
export const checkErrorMayClearLater = (
  category: VerificationCheckErrorCategory,
): boolean => PROVIDER_DID_NOT_RESPOND.includes(category);

/** `check` is a check's label, or several joined into one phrase. */
export const checkErrorMessage = (
  category: VerificationCheckErrorCategory,
  check: string,
): string =>
  checkErrorMayClearLater(category)
    ? `The service behind ${check} didn't respond. Try again later.`
    : `${check} couldn't run because of a problem on our side, not with this record.`;

/** A check whose provider does not serve the record's country (Not Covered). */
export const NOT_COVERED_LABEL = 'Not available for this country';

/** A skip reason meaning a detail on the record can't be checked. */
export type NeedsInputReason = 'invalid_domain' | 'invalid_email' | 'invalid_phone';

/** Why a check was skipped because a detail on the record can't be checked. */
export const NEEDS_INPUT_REASONS: Record<NeedsInputReason, string> = {
  invalid_domain:
    "the website on this record isn't a domain that can be checked",
  invalid_email:
    "the email on this record isn't an address that can be checked",
  invalid_phone: "the phone on this record isn't a number that can be checked",
};

export const listSentence = (items: string[]): string => {
  if (items.length <= 1) return items.join('');
  return `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`;
};

/**
 * The verification page's check rows each Verification Check feeds. The page
 * groups domain age and reputation into one row, and one location lookup
 * answers two rows.
 */
const CHECK_IDS_BY_VERIFICATION_TYPE = {
  domain_age: ['domain-contact'],
  domain_reputation: ['domain-contact'],
  website_quality: ['website-contact'],
  email_verify: ['email-verification'],
  phone_verify: ['phone-validation'],
  places_verification: ['listing-consistency', 'address-facility'],
  cbr_registry: ['business-registration'],
};

export type VerificationCheckType = keyof typeof CHECK_IDS_BY_VERIFICATION_TYPE;

const checkIdsFor = (verificationType: string): string[] =>
  (CHECK_IDS_BY_VERIFICATION_TYPE as Record<string, string[]>)[
    verificationType
  ] || [];

/** The Verification Checks that feed one page row. */
export const verificationCheckTypesFor = (
  checkId: string,
): VerificationCheckType[] =>
  (
    Object.keys(CHECK_IDS_BY_VERIFICATION_TYPE) as VerificationCheckType[]
  ).filter((type) => checkIdsFor(type).includes(checkId));

/** The page rows a run's Verification Checks feed, in page order. */
export const pageRowsFedBy = (
  runChecks: VerificationRunCheck[],
  checks: VerificationReportCheck[],
): VerificationReportCheck[] => {
  const ids = new Set(
    runChecks.flatMap((check) => checkIdsFor(check.verificationType)),
  );
  return checks.filter((check) => ids.has(check.id));
};

/** The run could not produce this check's result. */
export const endedInCheckError = (check: VerificationRunCheck): boolean =>
  check.status === 'failed' || check.status === 'unavailable';

// A service that predates errorCategory reports a missing key as
// 'unavailable'; anything else it could not run is unexplained.
const runCheckErrorCategory = (
  check: VerificationRunCheck,
): VerificationCheckErrorCategory =>
  check.errorCategory ||
  (check.status === 'unavailable' ? 'not_configured' : 'unexpected');

/**
 * A row's Check Error after a run. The record normally carries the error the
 * run stored; the run's own category stands in when storing it failed too.
 * A row Not Covered for the record's country never has one.
 */
export const runCheckErrorFor = (
  row: VerificationReportCheck,
  runChecks: VerificationRunCheck[],
): VerificationReportCheckError | null => {
  if (row.tone === 'unavailable') return null;
  if (row.error) return row.error;
  const runCheck = runChecks.find(
    (check) =>
      endedInCheckError(check) &&
      checkIdsFor(check.verificationType).includes(row.id),
  );
  if (!runCheck) return null;
  const category = runCheckErrorCategory(runCheck);
  return {
    category: category,
    occurredAt: '',
    message: checkErrorMessage(category, row.label),
  };
};

/** One message per kind of error, naming its checks in the order given. */
export const checkErrorSentences = (
  failing: Array<{ label: string; category: VerificationCheckErrorCategory }>,
): string[] => {
  const groups: Array<{
    category: VerificationCheckErrorCategory;
    labels: string[];
  }> = [];
  failing.forEach(({ label, category }) => {
    const group = groups.find(
      (candidate) =>
        checkErrorMayClearLater(candidate.category) ===
        checkErrorMayClearLater(category),
    );
    if (group) group.labels.push(label);
    else groups.push({ category: category, labels: [label] });
  });
  return groups.map((group) =>
    checkErrorMessage(group.category, listSentence(group.labels)),
  );
};

/**
 * The snackbar a finished Verification Run ends with, in the same words the
 * page uses for the Check Errors it stored. `checks` are built from the record
 * as the run saved it, so a part a stored result is missing is named too.
 */
export const describeVerificationRunFinish = ({
  runChecks,
  checks,
  entityName,
}: {
  runChecks: VerificationRunCheck[];
  checks: VerificationReportCheck[];
  entityName: string;
}): { variant: 'success' | 'warning' | 'error'; message: string } => {
  const completed = runChecks.some((check) => check.status === 'completed');
  const failing = pageRowsFedBy(
    runChecks.filter((check) => check.status !== 'skipped'),
    checks,
  ).flatMap((row) => {
    const error = runCheckErrorFor(row, runChecks);
    return error ? [{ label: row.label, category: error.category }] : [];
  });
  const needsInput = (Object.keys(NEEDS_INPUT_REASONS) as NeedsInputReason[])
    .map((reason) => {
      const labels = pageRowsFedBy(
        runChecks.filter(
          (check) => check.status === 'skipped' && check.reason === reason,
        ),
        checks,
      ).map((row) => row.label);
      return labels.length
        ? `${listSentence(labels)} ${
            labels.length > 1 ? 'need' : 'needs'
          } input: ${NEEDS_INPUT_REASONS[reason]}.`
        : '';
    })
    .filter(Boolean)
    .join(' ');

  if (!failing.length) {
    return {
      variant: needsInput ? 'warning' : 'success',
      message:
        `Business verification finished for ${entityName}. ${needsInput}`.trim(),
    };
  }
  return {
    variant: completed ? 'warning' : 'error',
    message: [
      ...checkErrorSentences(failing),
      needsInput,
      completed
        ? `The other checks on ${entityName} were updated.`
        : `Nothing on ${entityName} was updated.`,
    ]
      .filter(Boolean)
      .join(' '),
  };
};
